import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Badge,
  makeStyles,
  tokens,
  Tab,
  TabList,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import {
  PeopleRegular,
  MailRegular,
  ChatRegular,
  EyeRegular,
  CursorClickRegular,
  ArrowSyncRegular,
  PersonRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import DataTable, { type Column } from '../components/DataTable';
import { CardSkeleton } from '../components/LoadingSkeleton';
import {
  getOutreachStats,
  getOutreachHistory,
  getClientEngagement,
  type ClientOutreach,
} from '../services/api';
import { OutreachStats, OutreachActivity } from '../types';

const currentYear = new Date().getFullYear();
const defaultTaxYear = currentYear - 1;
const yearOptions = [defaultTaxYear, defaultTaxYear - 1, defaultTaxYear - 2];

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  },
  statCard: {
    padding: '20px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    boxShadow: tokens.shadow4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  statIcon: {
    fontSize: tokens.fontSizeHero700,
    marginBottom: '8px',
  },
  statValue: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  statPercent: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  chartContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    boxShadow: tokens.shadow4,
    padding: '20px',
  },
  chartTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: '16px',
  },
  chartPlaceholder: {
    height: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '4px',
    color: tokens.colorNeutralForeground3,
  },
  barContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  barLabel: {
    width: '80px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textAlign: 'right',
  },
  barTrack: {
    flex: 1,
    height: '24px',
    backgroundColor: tokens.colorNeutralBackground4,
    borderRadius: '4px',
    overflow: 'hidden',
    display: 'flex',
    gap: '4px',
  },
  barEmail: {
    backgroundColor: tokens.colorBrandBackground,
    height: '100%',
    transition: 'width 0.3s ease',
  },
  barSms: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    height: '100%',
    transition: 'width 0.3s ease',
  },
  barValue: {
    width: '60px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  legend: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase200,
  },
  legendDot: {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  activityIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase300,
  },
  activityContent: {
    flex: 1,
  },
  activityTime: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
  },
});

export default function OutreachDashboardPage() {
  const styles = useStyles();
  const [selectedYear, setSelectedYear] = useState(defaultTaxYear);
  const [selectedTab, setSelectedTab] = useState<string>('overview');

  // Fetch outreach stats
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery<OutreachStats>({
    queryKey: ['outreachStats', selectedYear],
    queryFn: () => getOutreachStats(selectedYear),
  });

  // Fetch outreach history
  const { data: history, isLoading: historyLoading } = useQuery<ClientOutreach[]>({
    queryKey: ['outreachHistory', selectedYear],
    queryFn: () => getOutreachHistory(selectedYear),
  });

  // Fetch client engagement
  const { data: engagement, isLoading: engagementLoading } = useQuery({
    queryKey: ['clientEngagement', selectedYear],
    queryFn: () => getClientEngagement(selectedYear),
  });

  const isLoading = statsLoading || historyLoading || engagementLoading;

  // Calculate max value for bar chart scaling
  const maxByDay = stats?.byDay ? Math.max(...stats.byDay.map(d => d.total), 1) : 1;

  const getActivityIcon = (type: string, channel: string) => {
    const iconStyle = {
      backgroundColor:
        type === 'email_opened' ? tokens.colorPaletteYellowBackground2 :
        type === 'link_clicked' ? tokens.colorPaletteGreenBackground2 :
        channel === 'email' ? tokens.colorBrandBackground2 :
        tokens.colorPaletteGreenBackground1,
    };

    const Icon =
      type === 'email_opened' ? EyeRegular :
      type === 'link_clicked' ? CursorClickRegular :
      channel === 'email' ? MailRegular : ChatRegular;

    return (
      <div className={styles.activityIcon} style={iconStyle}>
        <Icon />
      </div>
    );
  };

  const getActivityText = (activity: OutreachActivity) => {
    switch (activity.activityType) {
      case 'email_sent': return 'Email sent';
      case 'sms_sent': return 'SMS sent';
      case 'email_opened': return 'Opened email';
      case 'link_clicked': return 'Clicked link';
      case 'replied': return 'Replied';
      default: return activity.activityType;
    }
  };

  const engagementColumns: Column<{
    clientId: string;
    clientName: string;
    email?: string;
    phone?: string;
    emailsReceived: number;
    smsReceived: number;
    emailsOpened: number;
    linksClicked: number;
    lastActivity?: string;
  }>[] = [
    {
      key: 'clientName',
      header: 'Client',
      sortable: true,
      render: (client) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <PersonRegular />
          <div>
            <div style={{ fontWeight: 500 }}>{client.clientName}</div>
            <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>
              {client.email || client.phone || '-'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'emailsReceived',
      header: 'Emails',
      sortable: true,
      render: (client) => (
        <Badge appearance="outline" color="brand">{client.emailsReceived}</Badge>
      ),
    },
    {
      key: 'smsReceived',
      header: 'SMS',
      sortable: true,
      render: (client) => (
        <Badge appearance="outline" color="success">{client.smsReceived}</Badge>
      ),
    },
    {
      key: 'emailsOpened',
      header: 'Opened',
      sortable: true,
      render: (client) => client.emailsOpened > 0 ? (
        <Badge appearance="filled" color="warning">{client.emailsOpened}</Badge>
      ) : '-',
    },
    {
      key: 'linksClicked',
      header: 'Clicks',
      sortable: true,
      render: (client) => client.linksClicked > 0 ? (
        <Badge appearance="filled" color="success">{client.linksClicked}</Badge>
      ) : '-',
    },
    {
      key: 'lastActivity',
      header: 'Last Activity',
      sortable: true,
      getValue: (client) => client.lastActivity ? new Date(client.lastActivity).getTime() : 0,
      render: (client) => client.lastActivity
        ? format(new Date(client.lastActivity), 'MMM d, yyyy')
        : '-',
    },
  ];

  if (isLoading) {
    return <CardSkeleton />;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Outreach Dashboard</h2>
          <p>Analytics and reporting for client outreach campaigns</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Dropdown
            value={selectedYear.toString()}
            onOptionSelect={(_, data) => setSelectedYear(Number(data.optionValue))}
            style={{ minWidth: '120px' }}
          >
            {yearOptions.map(year => (
              <Option key={year} value={year.toString()} text={`Tax Year ${year}`}>
                Tax Year {year}
              </Option>
            ))}
          </Dropdown>
          <Button
            appearance="subtle"
            icon={<ArrowSyncRegular />}
            onClick={() => refetchStats()}
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className={styles.container}>
        {/* Stats Cards */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <PeopleRegular className={styles.statIcon} style={{ color: tokens.colorBrandForeground1 }} />
            <span className={styles.statValue} style={{ color: tokens.colorBrandForeground1 }}>
              {stats?.totalContacted || 0}
            </span>
            <span className={styles.statLabel}>Total Contacted</span>
          </div>

          <div className={styles.statCard}>
            <MailRegular className={styles.statIcon} style={{ color: tokens.colorBrandForeground1 }} />
            <span className={styles.statValue} style={{ color: tokens.colorBrandForeground1 }}>
              {stats?.emailsSent || 0}
            </span>
            <span className={styles.statLabel}>Emails Sent</span>
          </div>

          <div className={styles.statCard}>
            <ChatRegular className={styles.statIcon} style={{ color: tokens.colorPaletteGreenForeground1 }} />
            <span className={styles.statValue} style={{ color: tokens.colorPaletteGreenForeground1 }}>
              {stats?.smsSent || 0}
            </span>
            <span className={styles.statLabel}>SMS Sent</span>
          </div>

          <div className={styles.statCard}>
            <EyeRegular className={styles.statIcon} style={{ color: tokens.colorPaletteYellowForeground1 }} />
            <span className={styles.statValue} style={{ color: tokens.colorPaletteYellowForeground1 }}>
              {((stats?.openRate || 0) * 100).toFixed(0)}%
            </span>
            <span className={styles.statLabel}>Open Rate</span>
          </div>

          <div className={styles.statCard}>
            <CursorClickRegular className={styles.statIcon} style={{ color: tokens.colorPaletteGreenForeground1 }} />
            <span className={styles.statValue} style={{ color: tokens.colorPaletteGreenForeground1 }}>
              {((stats?.clickRate || 0) * 100).toFixed(0)}%
            </span>
            <span className={styles.statLabel}>Click Rate</span>
          </div>
        </div>

        {/* Tabs */}
        <TabList
          selectedValue={selectedTab}
          onTabSelect={(_, data) => setSelectedTab(data.value as string)}
        >
          <Tab value="overview">Overview</Tab>
          <Tab value="engagement">Client Engagement</Tab>
          <Tab value="history">Outreach History</Tab>
        </TabList>

        {selectedTab === 'overview' && (
          <div className={styles.twoColumnGrid}>
            {/* Outreach by Day Chart */}
            <div className={styles.chartContainer}>
              <h3 className={styles.chartTitle}>Outreach by Day</h3>
              <div className={styles.legend}>
                <div className={styles.legendItem}>
                  <div className={styles.legendDot} style={{ backgroundColor: tokens.colorBrandBackground }} />
                  <span>Email</span>
                </div>
                <div className={styles.legendItem}>
                  <div className={styles.legendDot} style={{ backgroundColor: tokens.colorPaletteGreenBackground2 }} />
                  <span>SMS</span>
                </div>
              </div>
              {stats?.byDay && stats.byDay.length > 0 ? (
                <div className={styles.barContainer}>
                  {stats.byDay.slice(-7).map((day) => (
                    <div key={day.date} className={styles.barRow}>
                      <span className={styles.barLabel}>
                        {format(new Date(day.date), 'MMM d')}
                      </span>
                      <div className={styles.barTrack}>
                        <div
                          className={styles.barEmail}
                          style={{ width: `${(day.emails / maxByDay) * 100}%` }}
                        />
                        <div
                          className={styles.barSms}
                          style={{ width: `${(day.sms / maxByDay) * 100}%` }}
                        />
                      </div>
                      <span className={styles.barValue}>{day.total}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.chartPlaceholder}>
                  No data available for the selected period
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className={styles.chartContainer}>
              <h3 className={styles.chartTitle}>Recent Activity</h3>
              {stats?.recentActivity && stats.recentActivity.length > 0 ? (
                <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {stats.recentActivity.slice(0, 10).map((activity) => (
                    <div key={activity.id} className={styles.activityItem}>
                      {getActivityIcon(activity.activityType, activity.channel)}
                      <div className={styles.activityContent}>
                        <div>
                          <strong>{activity.clientName}</strong> - {getActivityText(activity)}
                        </div>
                        <div className={styles.activityTime}>
                          {format(new Date(activity.timestamp), 'MMM d, yyyy h:mm a')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.chartPlaceholder}>
                  No recent activity
                </div>
              )}
            </div>
          </div>
        )}

        {selectedTab === 'engagement' && (
          <div className="card">
            <DataTable
              data={engagement || []}
              columns={engagementColumns}
              rowKey="clientId"
              searchable
              searchPlaceholder="Search clients..."
              searchKeys={['clientName', 'email']}
              emptyMessage="No client engagement data available."
              initialSort={{ column: 'linksClicked', direction: 'desc' }}
              paginated
            />
          </div>
        )}

        {selectedTab === 'history' && (
          <div className="card">
            <DataTable
              data={history || []}
              columns={[
                {
                  key: 'clientName',
                  header: 'Client',
                  sortable: true,
                  render: (outreach) => (
                    <div>
                      <div style={{ fontWeight: 500 }}>{outreach.clientName}</div>
                      <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>
                        {outreach.clientEmail}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'sentVia',
                  header: 'Channel',
                  sortable: true,
                  render: (outreach) => (
                    <Badge
                      appearance="outline"
                      color={outreach.sentVia === 'email' ? 'brand' : 'success'}
                      icon={outreach.sentVia === 'email' ? <MailRegular /> : <ChatRegular />}
                    >
                      {outreach.sentVia === 'email' ? 'Email' : 'SMS'}
                    </Badge>
                  ),
                },
                {
                  key: 'subject',
                  header: 'Subject',
                  sortable: true,
                },
                {
                  key: 'sentAt',
                  header: 'Sent',
                  sortable: true,
                  getValue: (outreach) => new Date(outreach.sentAt).getTime(),
                  render: (outreach) => format(new Date(outreach.sentAt), 'MMM d, yyyy h:mm a'),
                },
                {
                  key: 'emailOpened',
                  header: 'Opened',
                  sortable: true,
                  render: (outreach) => outreach.emailOpened ? (
                    <Badge appearance="filled" color="success">Yes</Badge>
                  ) : (
                    <Badge appearance="outline" color="subtle">No</Badge>
                  ),
                },
                {
                  key: 'linkClicked',
                  header: 'Clicked',
                  sortable: true,
                  render: (outreach) => outreach.linkClicked ? (
                    <Badge appearance="filled" color="success">Yes</Badge>
                  ) : (
                    <Badge appearance="outline" color="subtle">No</Badge>
                  ),
                },
              ] as Column<ClientOutreach>[]}
              rowKey="id"
              searchable
              searchPlaceholder="Search outreach history..."
              searchKeys={['clientName', 'clientEmail', 'subject']}
              emptyMessage="No outreach history found."
              initialSort={{ column: 'sentAt', direction: 'desc' }}
              paginated
            />
          </div>
        )}
      </div>
    </div>
  );
}
